import type { KycStatus } from '../types';

// ============================================================
// AGRIDA - Centralized Status System v2
// All status enums, labels, colors, filter options in one place.
// Single source of truth for ALL status display across the app.
// ============================================================

// ═══════════════════════════════════════════════════════════════
// ENUMS
// ═══════════════════════════════════════════════════════════════

// ─── eKYC ───────────────────────────────────────────────────
export const EKYC_STATUS = {
  REGISTERED: 'Chưa eKYC',
  PENDING: 'Đang xác thực',
  VERIFIED: 'Đã xác thực',
  NEED_MORE: 'Cần bổ sung',
  FAILED: 'Thất bại',
} as const;

export type EkycStatus = (typeof EKYC_STATUS)[keyof typeof EKYC_STATUS];


// ─── Risk Profile ────────────────────────────────────────────
export const RISK_LEVEL = {
  GOOD: 'Tốt',
  FAIR: 'Khá',
  WATCH: 'Cần chú ý',
  HIGH_RISK: 'Rủi ro cao',
  RISK: 'Nguy cơ',
  FAIL: 'Không đạt',
  NOT_CALCULATED: 'Chưa tính',
} as const;

export type RiskLevel = (typeof RISK_LEVEL)[keyof typeof RISK_LEVEL];

// ─── Loan Type ───────────────────────────────────────────────
export const LOAN_TYPE = {
  MONEY: 'Tiền',
  MATERIAL: 'Vật tư',
} as const;

export type LoanType = (typeof LOAN_TYPE)[keyof typeof LOAN_TYPE];

// ─── Contract ────────────────────────────────────────────────
export const CONTRACT_STATUS = {
  DRAFT: 'Nháp',
  PENDING_SIGN: 'Chờ ký',
  ACTIVE: 'Đang hiệu lực',
  EXPIRING_SOON: 'Sắp hết hạn',
  EXPIRED: 'Hết hạn',
  MISSING_DOCS: 'Thiếu tài liệu',
  PAUSED: 'Tạm dừng',
  CANCELLED: 'Hủy',
} as const;

export type ContractStatus = (typeof CONTRACT_STATUS)[keyof typeof CONTRACT_STATUS];

// ─── HTX Limit ───────────────────────────────────────────────
export const HTX_LIMIT_STATUS = {
  NONE: 'Chưa có',
  PENDING_BANK: 'Chờ ngân hàng duyệt',
  APPROVED: 'Đã duyệt',
  NEED_MORE: 'Cần bổ sung',
  REJECTED: 'Từ chối',
  EXPIRING_SOON: 'Sắp hết hạn',
  EXPIRED: 'Hết hạn',
  LOCKED: 'Tạm khóa',
} as const;

export type HtxLimitStatus = (typeof HTX_LIMIT_STATUS)[keyof typeof HTX_LIMIT_STATUS];

// ─── Farmer Limit ────────────────────────────────────────────
export const FARMER_LIMIT_STATUS = {
  NONE: 'Chưa có',
  PENDING: 'Chờ duyệt',
  APPROVED: 'Đã duyệt',
  ADJUSTING: 'Điều chỉnh',
  NEED_MORE: 'Cần bổ sung',
  REJECTED: 'Từ chối',
  LOCKED: 'Tạm khóa',
  EXPIRED: 'Hết hiệu lực',
} as const;

export type FarmerLimitStatus = (typeof FARMER_LIMIT_STATUS)[keyof typeof FARMER_LIMIT_STATUS];

// ─── Advance Request / Loan ──────────────────────────────────
export const LOAN_STATUS = {
  PENDING_CHECK: 'Chờ kiểm tra',
  NEED_MORE: 'Cần bổ sung',
  NOT_ELIGIBLE: 'Không đáp ứng',
  APPROVED: 'Đã duyệt',
  REJECTED: 'Từ chối',
  PENDING_DISBURSEMENT: 'Chờ giải ngân',
  DISBURSED: 'Đã giải ngân',
  DISBURSEMENT_ERROR: 'Giải ngân lỗi',
  SETTLED: 'Đã tất toán',
  OVERDUE: 'Quá hạn',
} as const;

export type LoanStatus = (typeof LOAN_STATUS)[keyof typeof LOAN_STATUS];

// ─── Disbursement ────────────────────────────────────────────
export const DISBURSEMENT_STATUS = {
  NOT_SENT: 'Chưa gửi',
  SENT_TO_BANK: 'Đã gửi ngân hàng',
  PROCESSING: 'Đang xử lý',
  SUCCESS: 'Thành công',
  FAILED: 'Thất bại',
  CANCELLED: 'Đã hủy',
} as const;

export type DisbursementStatus = (typeof DISBURSEMENT_STATUS)[keyof typeof DISBURSEMENT_STATUS];

// ─── Debt ────────────────────────────────────────────────────
export const DEBT_STATUS = {
  ACTIVE: 'Đang vay',
  DUE_SOON: 'Sắp đến hạn',
  DUE: 'Đến hạn',
  PENDING_PAYMENT: 'Chờ thanh toán',
  PAYMENT_ERROR: 'Chờ xử lý lỗi thanh toán',
  OVERDUE: 'Quá hạn',
  SETTLED: 'Đã tất toán',
  COLLECTION_ERROR: 'Thu nợ lỗi',
} as const;

export type DebtStatus = (typeof DEBT_STATUS)[keyof typeof DEBT_STATUS];

// ─── Payment ─────────────────────────────────────────────────
export const PAYMENT_STATUS = {
  PENDING: 'Chờ thanh toán',
  PAID: 'Đã thanh toán',
  PARTIAL: 'Thanh toán thiếu',
  BANK_ERROR: 'Lỗi ngân hàng',
  CANCELLED: 'Đã hủy',
} as const;

export type PaymentStatus = (typeof PAYMENT_STATUS)[keyof typeof PAYMENT_STATUS];

// ─── Transaction ─────────────────────────────────────────────
export const TRANSACTION_STATUS = {
  COMPLETED: 'Hoàn thành',
  PENDING: 'Chờ xác nhận',
  CANCELLED: 'Hủy',
} as const;

export type TransactionStatus = (typeof TRANSACTION_STATUS)[keyof typeof TRANSACTION_STATUS];

// ─── Farmer ──────────────────────────────────────────────────
export const FARMER_STATUS = {
  ACTIVE: 'Hoạt động',
  PAUSED: 'Tạm dừng',
  STOPPED: 'Ngừng',
} as const;

// ─── HTX Status ──────────────────────────────────────────────
export const HTX_STATUS = {
  ACTIVE: 'Đang hoạt động',
  WATCH: 'Cần chú ý',
  RISK: 'Nguy cơ',
  SUSPENDED: 'Tạm ngưng',
  NOT_ELIGIBLE: 'Không đủ điều kiện vay',
} as const;

// ─── Field ───────────────────────────────────────────────────
export const FIELD_STATUS = {
  CULTIVATING: 'Đang canh tác',
  PAUSED: 'Tạm dừng',
  VACANT: 'Bỏ trống',
} as const;

// ─── Season ──────────────────────────────────────────────────
export const SEASON_STATUS = {
  PREPARING: 'Chuẩn bị',
  CULTIVATING: 'Đang canh tác',
  HARVESTING_SOON: 'Sắp thu hoạch',
  HARVESTED: 'Đã thu hoạch',
  FINISHED: 'Kết thúc',
  AT_RISK: 'Có rủi ro',
} as const;

// ─── Harvest ─────────────────────────────────────────────────
export const HARVEST_STATUS = {
  PLANNED: 'Dự kiến',
  FARMER_UPDATED: 'Nông hộ đã cập nhật',
  PENDING_HTX_CONFIRM: 'Chờ HTX xác nhận',
  HTX_CONFIRMED: 'HTX đã xác nhận',
  LOW_YIELD: 'Sản lượng thấp',
  COMPLETED: 'Hoàn thành',
  CANCELLED: 'Hủy',
} as const;

/** A confirmed harvest under this share of its own expected quantity is flagged
 * "Sản lượng thấp". Mirrors LOW_YIELD_RATIO in the backend's HarvestService,
 * which is what actually sets the status — keep the two in step so the UI never
 * promises a flag the server will not raise. */
export const LOW_YIELD_RATIO = 0.7;

// ─── Supply Item ─────────────────────────────────────────────
export const SUPPLY_ITEM_STATUS = {
  ADEQUATE: 'Đủ',
  LOW: 'Sắp hết',
  OUT: 'Hết hàng',
  DISCONTINUED: 'Ngừng cung ứng',
} as const;

// ─── Material ────────────────────────────────────────────────
export const MATERIAL_STATUS = {
  ADEQUATE: 'Đủ',
  LOW: 'Sắp hết',
  SHORTAGE: 'Thiếu',
  IN_USE: 'Đang sử dụng',
  DEPLETED: 'Đã hết',
  ISSUED: 'Đã cấp',
  RETURNED: 'Đã trả',
  PENDING: 'Chờ cấp',
} as const;

// ─── Supply Transaction ──────────────────────────────────────
export const SUPPLY_TRANSACTION_STATUS = {
  ISSUED: 'Đã cấp',
  PENDING: 'Chờ duyệt',
  DELIVERING: 'Đang giao',
} as const;

// ─── HTX Loan Source ─────────────────────────────────────────
export const HTX_SOURCE_STATUS = {
  ACTIVE: 'Đang hiệu lực',
  EXPIRING_SOON: 'Sắp hết hạn',
  ENDED: 'Đã kết thúc',
  PAUSED: 'Tạm dừng',
} as const;

export const HTX_SOURCE_TYPE = {
  BANK: 'Ngân hàng',
  HTX_FUND: 'Quỹ HTX',
  PARTNER: 'Đối tác',
  INTERNAL: 'Nguồn nội bộ',
  OTHER: 'Khác',
} as const;

// ─── Finance Source ──────────────────────────────────────────
export const FINANCE_TYPE = {
  THU: 'Thu',
  CHI: 'Chi',
} as const;

export const FINANCE_CATEGORY = {
  DISBURSEMENT: 'Giải ngân',
  REPAYMENT: 'Trả nợ',
  SUPPLY: 'Cấp vật tư',
  ADJUST: 'Điều chỉnh',
  OTHER: 'Khác',
} as const;

// ─── Supply History ──────────────────────────────────────────
export const SUPPLY_HISTORY_TYPE = {
  NHAP: 'Nhập',
  XUAT: 'Xuất',
  ADJUST: 'Điều chỉnh',
} as const;

// ─── Notification ────────────────────────────────────────────
export const NOTIFICATION_TYPE = {
  DISBURSE_SUCCESS: 'disburse_success',
  DISBURSE_FAILED: 'disburse_failed',
  LOAN_APPROVED: 'loan_approved',
  LOAN_REJECTED: 'loan_rejected',
  DEBT_DUE: 'debt_due',
  DEBT_OVERDUE: 'debt_overdue',
  GENERAL: 'general',
} as const;

// ═══════════════════════════════════════════════════════════════
// COLOR SYSTEM
// ═══════════════════════════════════════════════════════════════

type ColorPair = { bg: string; text: string };

const C = {
  green:   { bg: 'bg-emerald-100', text: 'text-emerald-700' } as ColorPair,
  agrida:  { bg: 'bg-agrida-100',  text: 'text-agrida-700' }  as ColorPair,
  amber:   { bg: 'bg-amber-100',   text: 'text-amber-700' }   as ColorPair,
  orange:  { bg: 'bg-orange-100',  text: 'text-orange-700' }  as ColorPair,
  red:     { bg: 'bg-red-100',     text: 'text-red-700' }     as ColorPair,
  rose:    { bg: 'bg-rose-100',    text: 'text-rose-700' }    as ColorPair,
  sky:     { bg: 'bg-sky-100',     text: 'text-sky-700' }     as ColorPair,
  slate:   { bg: 'bg-slate-100',   text: 'text-slate-600' }   as ColorPair,
  neutral: { bg: 'bg-slate-100',   text: 'text-slate-500' }   as ColorPair,
};

// Each domain gets its own color map to avoid TS1117 duplicate keys
// (multiple status enums share the same Vietnamese label values)

const EKYC_COLORS: Record<string, ColorPair> = {
  [EKYC_STATUS.REGISTERED]: C.slate,
  [EKYC_STATUS.PENDING]:    C.sky,
  [EKYC_STATUS.VERIFIED]:   C.green,
  [EKYC_STATUS.NEED_MORE]:  C.amber,
  [EKYC_STATUS.FAILED]:     C.red,
};

const RISK_COLORS: Record<string, ColorPair> = {
  [RISK_LEVEL.GOOD]:           C.agrida,
  [RISK_LEVEL.FAIR]:           { bg: 'bg-teal-100', text: 'text-teal-700' } as ColorPair,
  [RISK_LEVEL.WATCH]:          C.amber,
  [RISK_LEVEL.HIGH_RISK]:      { bg: 'bg-orange-100', text: 'text-orange-700' } as ColorPair,
  [RISK_LEVEL.RISK]:           C.red,
  [RISK_LEVEL.FAIL]:           C.red,
  [RISK_LEVEL.NOT_CALCULATED]: C.neutral,
};

const LOAN_TYPE_COLORS: Record<string, ColorPair> = {
  [LOAN_TYPE.MONEY]:   C.sky,
  [LOAN_TYPE.MATERIAL]: C.green,
};

const CONTRACT_COLORS: Record<string, ColorPair> = {
  [CONTRACT_STATUS.DRAFT]:         C.slate,
  [CONTRACT_STATUS.PENDING_SIGN]:  C.sky,
  [CONTRACT_STATUS.ACTIVE]:        C.agrida,
  [CONTRACT_STATUS.EXPIRING_SOON]: C.amber,
  [CONTRACT_STATUS.EXPIRED]:       C.red,
  [CONTRACT_STATUS.MISSING_DOCS]:  C.amber,
  [CONTRACT_STATUS.PAUSED]:        C.amber,
  [CONTRACT_STATUS.CANCELLED]:     C.red,
};

const HTX_LIMIT_COLORS: Record<string, ColorPair> = {
  [HTX_LIMIT_STATUS.NONE]:         C.slate,
  [HTX_LIMIT_STATUS.PENDING_BANK]: C.sky,
  [HTX_LIMIT_STATUS.APPROVED]:     C.green,
  [HTX_LIMIT_STATUS.NEED_MORE]:    C.amber,
  [HTX_LIMIT_STATUS.REJECTED]:     C.red,
  [HTX_LIMIT_STATUS.EXPIRING_SOON]: C.amber,
  [HTX_LIMIT_STATUS.EXPIRED]:      C.red,
  [HTX_LIMIT_STATUS.LOCKED]:       C.slate,
};

const FARMER_LIMIT_COLORS: Record<string, ColorPair> = {
  [FARMER_LIMIT_STATUS.NONE]:      C.slate,
  [FARMER_LIMIT_STATUS.PENDING]:   C.sky,
  [FARMER_LIMIT_STATUS.APPROVED]:  C.green,
  [FARMER_LIMIT_STATUS.ADJUSTING]: C.amber,
  [FARMER_LIMIT_STATUS.NEED_MORE]: C.amber,
  [FARMER_LIMIT_STATUS.REJECTED]:  C.red,
  [FARMER_LIMIT_STATUS.LOCKED]:    C.slate,
  [FARMER_LIMIT_STATUS.EXPIRED]:   C.red,
};

const LOAN_COLORS: Record<string, ColorPair> = {
  [LOAN_STATUS.PENDING_CHECK]:       C.orange,
  [LOAN_STATUS.NEED_MORE]:           C.amber,
  [LOAN_STATUS.NOT_ELIGIBLE]:        C.red,
  [LOAN_STATUS.APPROVED]:            C.agrida,
  [LOAN_STATUS.REJECTED]:            C.red,
  [LOAN_STATUS.PENDING_DISBURSEMENT]: C.sky,
  [LOAN_STATUS.DISBURSED]:           C.green,
  [LOAN_STATUS.DISBURSEMENT_ERROR]:  C.red,
  [LOAN_STATUS.SETTLED]:             C.green,
  [LOAN_STATUS.OVERDUE]:             C.red,
  // New statuses
  'approved_locked':                 { bg: 'bg-sky-100', text: 'text-sky-700' } as ColorPair,
  'waiting_bank_response':           { bg: 'bg-sky-100', text: 'text-sky-700' } as ColorPair,
  'disbursed':                       { bg: 'bg-emerald-100', text: 'text-emerald-700' } as ColorPair,
  'disbursement_failed':             { bg: 'bg-red-100', text: 'text-red-700' } as ColorPair,
  'bank_timeout':                    { bg: 'bg-amber-100', text: 'text-amber-700' } as ColorPair,
  'lock_expired':                    { bg: 'bg-orange-100', text: 'text-orange-700' } as ColorPair,
};

const DISBURSEMENT_COLORS: Record<string, ColorPair> = {
  [DISBURSEMENT_STATUS.NOT_SENT]:     C.slate,
  [DISBURSEMENT_STATUS.SENT_TO_BANK]: C.sky,
  [DISBURSEMENT_STATUS.PROCESSING]:   C.sky,
  [DISBURSEMENT_STATUS.SUCCESS]:      C.green,
  [DISBURSEMENT_STATUS.FAILED]:       C.red,
  [DISBURSEMENT_STATUS.CANCELLED]:    C.slate,
};

const DEBT_COLORS: Record<string, ColorPair> = {
  [DEBT_STATUS.ACTIVE]:          C.sky,
  [DEBT_STATUS.DUE_SOON]:        C.amber,
  [DEBT_STATUS.DUE]:             C.orange,
  [DEBT_STATUS.PENDING_PAYMENT]:  C.sky,
  [DEBT_STATUS.PAYMENT_ERROR]:    C.rose,
  [DEBT_STATUS.OVERDUE]:         C.red,
  [DEBT_STATUS.SETTLED]:         C.green,
  [DEBT_STATUS.COLLECTION_ERROR]: C.red,
};

const PAYMENT_COLORS: Record<string, ColorPair> = {
  [PAYMENT_STATUS.PENDING]:   C.sky,
  [PAYMENT_STATUS.PAID]:      C.agrida,
  [PAYMENT_STATUS.PARTIAL]:   C.amber,
  [PAYMENT_STATUS.BANK_ERROR]: C.red,
  [PAYMENT_STATUS.CANCELLED]:  C.slate,
};

const TRANSACTION_COLORS: Record<string, ColorPair> = {
  [TRANSACTION_STATUS.COMPLETED]: C.agrida,
  [TRANSACTION_STATUS.PENDING]:   C.sky,
  [TRANSACTION_STATUS.CANCELLED]: C.red,
};

const FARMER_COLORS: Record<string, ColorPair> = {
  [FARMER_STATUS.ACTIVE]:  C.agrida,
  [FARMER_STATUS.PAUSED]:  C.amber,
  [FARMER_STATUS.STOPPED]: C.red,
};

const HTX_COLORS: Record<string, ColorPair> = {
  [HTX_STATUS.ACTIVE]:       C.agrida,
  [HTX_STATUS.WATCH]:        C.amber,
  [HTX_STATUS.RISK]:         C.red,
  [HTX_STATUS.SUSPENDED]:    C.slate,
  [HTX_STATUS.NOT_ELIGIBLE]: C.slate,
};

const FIELD_COLORS: Record<string, ColorPair> = {
  [FIELD_STATUS.CULTIVATING]: C.agrida,
  [FIELD_STATUS.PAUSED]:      C.amber,
  [FIELD_STATUS.VACANT]:      C.slate,
};

const SEASON_COLORS: Record<string, ColorPair> = {
  [SEASON_STATUS.PREPARING]:        C.sky,
  [SEASON_STATUS.CULTIVATING]:      C.agrida,
  [SEASON_STATUS.HARVESTING_SOON]:  C.amber,
  [SEASON_STATUS.HARVESTED]:        C.agrida,
  [SEASON_STATUS.FINISHED]:         C.slate,
  [SEASON_STATUS.AT_RISK]:          C.red,
};

const HARVEST_COLORS: Record<string, ColorPair> = {
  [HARVEST_STATUS.PLANNED]:              C.sky,
  [HARVEST_STATUS.FARMER_UPDATED]:       C.amber,
  [HARVEST_STATUS.PENDING_HTX_CONFIRM]:  C.sky,
  [HARVEST_STATUS.HTX_CONFIRMED]:        C.agrida,
  [HARVEST_STATUS.LOW_YIELD]:            C.red,
  [HARVEST_STATUS.COMPLETED]:            C.agrida,
  [HARVEST_STATUS.CANCELLED]:            C.red,
};

const SUPPLY_ITEM_COLORS: Record<string, ColorPair> = {
  [SUPPLY_ITEM_STATUS.ADEQUATE]:      C.agrida,
  [SUPPLY_ITEM_STATUS.LOW]:           C.amber,
  [SUPPLY_ITEM_STATUS.OUT]:           C.slate,
  [SUPPLY_ITEM_STATUS.DISCONTINUED]:  C.slate,
};

const MATERIAL_COLORS: Record<string, ColorPair> = {
  [MATERIAL_STATUS.ADEQUATE]:  C.agrida,
  [MATERIAL_STATUS.LOW]:       C.amber,
  [MATERIAL_STATUS.SHORTAGE]:  C.red,
  [MATERIAL_STATUS.IN_USE]:    C.sky,
  [MATERIAL_STATUS.DEPLETED]:  C.slate,
  [MATERIAL_STATUS.ISSUED]:    C.agrida,
  [MATERIAL_STATUS.RETURNED]:  C.slate,
  [MATERIAL_STATUS.PENDING]:   C.sky,
};

const SUPPLY_TRANSACTION_COLORS: Record<string, ColorPair> = {
  [SUPPLY_TRANSACTION_STATUS.ISSUED]:     C.agrida,
  [SUPPLY_TRANSACTION_STATUS.PENDING]:    C.sky,
  [SUPPLY_TRANSACTION_STATUS.DELIVERING]: C.sky,
};

const HTX_SOURCE_TYPE_COLORS: Record<string, ColorPair> = {
  [HTX_SOURCE_TYPE.BANK]:     C.sky,
  [HTX_SOURCE_TYPE.HTX_FUND]: C.agrida,
  [HTX_SOURCE_TYPE.PARTNER]:  C.amber,
  [HTX_SOURCE_TYPE.INTERNAL]: C.slate,
  [HTX_SOURCE_TYPE.OTHER]:    C.slate,
};

const HTX_SOURCE_STATUS_COLORS: Record<string, ColorPair> = {
  [HTX_SOURCE_STATUS.ACTIVE]:        C.agrida,
  [HTX_SOURCE_STATUS.EXPIRING_SOON]: C.amber,
  [HTX_SOURCE_STATUS.ENDED]:         C.slate,
  [HTX_SOURCE_STATUS.PAUSED]:        C.amber,
};

const FINANCE_COLORS: Record<string, ColorPair> = {
  [FINANCE_TYPE.THU]: C.agrida,
  [FINANCE_TYPE.CHI]: C.red,
};

const SUPPLY_HISTORY_COLORS: Record<string, ColorPair> = {
  [SUPPLY_HISTORY_TYPE.NHAP]:  C.sky,
  [SUPPLY_HISTORY_TYPE.XUAT]:  C.amber,
  [SUPPLY_HISTORY_TYPE.ADJUST]: C.slate,
};

const MISC_COLORS: Record<string, ColorPair> = {
  'HTX cấp/ứng':      { bg: 'bg-sky-100', text: 'text-sky-700' },
  'Nông hộ tự mua':   C.slate,
};

const ALL_COLOR_MAPS: Record<string, ColorPair>[] = [
  EKYC_COLORS,
  RISK_COLORS,
  LOAN_TYPE_COLORS,
  CONTRACT_COLORS,
  HTX_LIMIT_COLORS,
  FARMER_LIMIT_COLORS,
  LOAN_COLORS,
  DISBURSEMENT_COLORS,
  DEBT_COLORS,
  PAYMENT_COLORS,
  TRANSACTION_COLORS,
  FARMER_COLORS,
  HTX_COLORS,
  FIELD_COLORS,
  SEASON_COLORS,
  HARVEST_COLORS,
  SUPPLY_ITEM_COLORS,
  MATERIAL_COLORS,
  SUPPLY_TRANSACTION_COLORS,
  HTX_SOURCE_TYPE_COLORS,
  HTX_SOURCE_STATUS_COLORS,
  FINANCE_COLORS,
  SUPPLY_HISTORY_COLORS,
  MISC_COLORS,
];

const FALLBACK_COLOR: ColorPair = { bg: 'bg-slate-100', text: 'text-slate-500' };

// ═══════════════════════════════════════════════════════════════
// PUBLIC API - Core Functions
// ═══════════════════════════════════════════════════════════════

/** Get Tailwind color pair for any status string. Returns { bg, text }. */
export function getStatusColor(status: string): ColorPair {
  // Check extended colors first (higher priority)
  if (Object.prototype.hasOwnProperty.call(LOAN_EXTENDED_COLORS, status)) {
    return LOAN_EXTENDED_COLORS[status];
  }
  for (const map of ALL_COLOR_MAPS) {
    if (Object.prototype.hasOwnProperty.call(map, status)) {
      return map[status];
    }
  }
  return FALLBACK_COLOR;
}

/**
 * Normalize a status label. Handles legacy aliases so all screens
 * show the same label regardless of what was stored historically.
 */
export function getStatusLabel(status: string): string {
  const ALIAS_MAP: Record<string, string> = {
    // Disbursement aliases
    'Đã gửi bank':              DISBURSEMENT_STATUS.SENT_TO_BANK,
    'Đã gửi NH':                DISBURSEMENT_STATUS.SENT_TO_BANK,
    'Gửi ngân hàng':            DISBURSEMENT_STATUS.SENT_TO_BANK,
    'Giải ngân thành công':     DISBURSEMENT_STATUS.SUCCESS,
    'Giải ngân thất bại':       DISBURSEMENT_STATUS.FAILED,
    'Lỗi giải ngân':           DISBURSEMENT_STATUS.FAILED,
    'Đã giải ngân thành công':  DISBURSEMENT_STATUS.SUCCESS,
    'Chưa giải ngân':           DISBURSEMENT_STATUS.NOT_SENT,

    // Loan aliases
    'Chờ HTX kiểm tra':         LOAN_STATUS.PENDING_CHECK,
    'Cần bổ sung thông tin':    LOAN_STATUS.NEED_MORE,
    'Không đủ điều kiện':       LOAN_STATUS.NOT_ELIGIBLE,
    'Đã duyệt vay':             LOAN_STATUS.APPROVED,
    'Không duyệt':              LOAN_STATUS.REJECTED,
    'Đang giải ngân':           LOAN_STATUS.PENDING_DISBURSEMENT,
    'Giải ngân xong':           LOAN_STATUS.DISBURSED,
    'Đã hoàn trả':              LOAN_STATUS.SETTLED,
    'Trễ hạn':                  LOAN_STATUS.OVERDUE,

    // Contract aliases
    'Còn hiệu lực':             CONTRACT_STATUS.ACTIVE,
    'Đã hoàn thành':            CONTRACT_STATUS.ACTIVE,
    'Đã thanh lý':              CONTRACT_STATUS.CANCELLED,
    'Hợp đồng hết hạn':         CONTRACT_STATUS.EXPIRED,

    // Debt aliases
    'Chưa trả':                 DEBT_STATUS.ACTIVE,
    'Đã trả':                   DEBT_STATUS.SETTLED,
    'Quá hạn thanh toán':       DEBT_STATUS.OVERDUE,
    'Chưa đến hạn':             DEBT_STATUS.ACTIVE,
    'Lỗi thanh toán':           DEBT_STATUS.PAYMENT_ERROR,

    // eKYC aliases
    'Chưa xác thực':            EKYC_STATUS.REGISTERED,
    'Đang chờ xác thực':        EKYC_STATUS.PENDING,
    'Xác thực thành công':      EKYC_STATUS.VERIFIED,
    'Bổ sung thông tin':        EKYC_STATUS.NEED_MORE,
    'Xác thực thất bại':        EKYC_STATUS.FAILED,

    // Risk aliases
    'Rủi ro thấp':              RISK_LEVEL.GOOD,
    'Rủi ro trung bình':        RISK_LEVEL.WATCH,
    'Rủi ro cao':               RISK_LEVEL.RISK,

    // Supply aliases
    'Còn hàng':                 SUPPLY_ITEM_STATUS.ADEQUATE,
    'Đã ngừng':                 SUPPLY_ITEM_STATUS.DISCONTINUED,

    // Special cases - keep as-is
    'Đã cấp vật tư':            'Đã cấp vật tư',
    // New status aliases
    'approved_locked':           'Đã duyệt (đang khóa)',
    'waiting_bank_response':     'Chờ NH phản hồi',
    'disbursed':                 'Đã giải ngân',
    'disbursement_failed':       'Giải ngân lỗi',
    'bank_timeout':              'NH không phản hồi',
    'lock_expired':              'Hết khóa HM',
  };

  return ALIAS_MAP[status] || status;
}

/** Get canonical { bg, text, label } for any status string. */
export function getStatusInfo(status: string): { bg: string; text: string; label: string } {
  const label = getStatusLabel(status);
  const color = getStatusColor(status);
  return { ...color, label };
}

/** Get the semantic category of a status for progress/usage coloring. */
export function getProgressColor(percent: number): { barColor: string; textColor: string } {
  if (percent > 100) return { barColor: 'bg-red-500', textColor: 'text-red-600' };
  if (percent > 80)  return { barColor: 'bg-red-400',   textColor: 'text-red-600' };
  if (percent > 60)  return { barColor: 'bg-amber-400', textColor: 'text-amber-600' };
  return              { barColor: 'bg-emerald-400', textColor: 'text-emerald-600' };
}

// ═══════════════════════════════════════════════════════════════
// PUBLIC API - Domain-specific Helpers
// ═══════════════════════════════════════════════════════════════

/** eKYC: get color for a KycStatus (internal key). */
export function getKycColor(status: string): ColorPair {
  const map: Record<string, ColorPair> = {
    registered:      C.slate,
    ekyc_pending:    C.sky,
    ekyc_verified:   C.green,
    ekyc_need_more:  C.amber,
    ekyc_failed:     C.red,
  };
  return map[status] || FALLBACK_COLOR;
}

/** eKYC: get display label for a KycStatus (internal key). */
export function getKycLabel(status: string): string {
  const map: Record<string, string> = {
    registered:     EKYC_STATUS.REGISTERED,
    ekyc_pending:   EKYC_STATUS.PENDING,
    ekyc_verified:  EKYC_STATUS.VERIFIED,
    ekyc_need_more: EKYC_STATUS.NEED_MORE,
    ekyc_failed:    EKYC_STATUS.FAILED,
  };
  return map[status] || 'Không xác định';
}

/** Risk: get badge color for a RiskLevel. */
export function getRiskColor(risk?: string): ColorPair {
  return getStatusColor(risk || '');
}

/** Credit score: get label. */
export function getCreditScoreLabel(score: number | null | undefined): string {
  if (score == null) return 'Chưa có điểm';
  if (score >= 760) return 'Tốt';
  if (score >= 650) return 'Khá';
  if (score >= 550) return 'Cần chú ý';
  if (score >= 450) return 'Rủi ro cao';
  return 'Nguy cơ';
}

/** Credit score: get text color. */
export function getCreditScoreColor(score: number | null | undefined): string {
  if (score == null) return 'text-slate-400';
  if (score >= 760) return 'text-emerald-700';
  if (score >= 650) return 'text-teal-700';
  if (score >= 550) return 'text-amber-600';
  if (score >= 450) return 'text-orange-600';
  return 'text-red-600';
}

/** Credit score: get badge background class. */
export function getCreditScoreBg(score: number | null | undefined): string {
  if (score == null) return 'bg-slate-100';
  if (score >= 760) return 'bg-emerald-100';
  if (score >= 650) return 'bg-teal-100';
  if (score >= 550) return 'bg-amber-100';
  if (score >= 450) return 'bg-orange-100';
  return 'bg-red-100';
}

/** Credit score: get border class. */
export function getCreditScoreBorder(score: number | null | undefined): string {
  if (score == null) return 'border-slate-200';
  if (score >= 760) return 'border-emerald-300';
  if (score >= 650) return 'border-teal-300';
  if (score >= 550) return 'border-amber-300';
  if (score >= 450) return 'border-orange-300';
  return 'border-red-300';
}

/** Loan type: get badge color for Tiền/Vật tư. */
export function getLoanTypeColor(type?: string): ColorPair {
  if (type === LOAN_TYPE.MONEY) return C.sky;
  if (type === LOAN_TYPE.MATERIAL) return C.green;
  return C.slate;
}

// ═══════════════════════════════════════════════════════════════
// FILTER OPTION HELPERS
// All filter dropdowns should use these to ensure consistent labels
// ═══════════════════════════════════════════════════════════════

export const EKYC_FILTER_OPTIONS = [
  { value: '', label: 'Tất cả eKYC' },
  ...Object.values(EKYC_STATUS).map((s) => ({ value: s, label: s })),
];

export const RISK_FILTER_OPTIONS = [
  { value: '', label: 'Tất cả rủi ro' },
  ...Object.values(RISK_LEVEL).map((s) => ({ value: s, label: s })),
];

export const LOAN_FILTER_OPTIONS = [
  { value: '', label: 'Tất cả trạng thái' },
  ...Object.values(LOAN_STATUS).map((s) => ({ value: s, label: s })),
];

export const CONTRACT_FILTER_OPTIONS = [
  { value: '', label: 'Tất cả trạng thái' },
  ...Object.values(CONTRACT_STATUS).map((s) => ({ value: s, label: s })),
];

export const DEBT_FILTER_OPTIONS = [
  { value: '', label: 'Tất cả trạng thái' },
  ...Object.values(DEBT_STATUS).map((s) => ({ value: s, label: s })),
];

export const DISBURSEMENT_FILTER_OPTIONS = [
  { value: '', label: 'Tất cả trạng thái' },
  ...Object.values(DISBURSEMENT_STATUS).map((s) => ({ value: s, label: s })),
];

export const HARVEST_FILTER_OPTIONS = [
  { value: '', label: 'Tất cả trạng thái' },
  ...Object.values(HARVEST_STATUS).map((s) => ({ value: s, label: s })),
];

export const SUPPLY_FILTER_OPTIONS = [
  { value: '', label: 'Tất cả trạng thái' },
  ...Object.values(SUPPLY_ITEM_STATUS).map((s) => ({ value: s, label: s })),
];

export const MATERIAL_FILTER_OPTIONS = [
  { value: '', label: 'Tất cả trạng thái' },
  ...Object.values(MATERIAL_STATUS).map((s) => ({ value: s, label: s })),
];

export const HTX_SOURCE_FILTER_OPTIONS = [
  { value: '', label: 'Tất cả trạng thái' },
  ...Object.values(HTX_SOURCE_STATUS).map((s) => ({ value: s, label: s })),
];

export const HTX_SOURCE_TYPE_OPTIONS = [
  { value: '', label: 'Tất cả loại' },
  ...Object.values(HTX_SOURCE_TYPE).map((s) => ({ value: s, label: s })),
];

export const FARMER_FILTER_OPTIONS = [
  { value: '', label: 'Tất cả trạng thái' },
  ...Object.values(FARMER_STATUS).map((s) => ({ value: s, label: s })),
];

export const FIELD_FILTER_OPTIONS = [
  { value: '', label: 'Tất cả trạng thái' },
  ...Object.values(FIELD_STATUS).map((s) => ({ value: s, label: s })),
];

export const SEASON_FILTER_OPTIONS = [
  { value: '', label: 'Tất cả trạng thái' },
  ...Object.values(SEASON_STATUS).map((s) => ({ value: s, label: s })),
];

export const PAYMENT_FILTER_OPTIONS = [
  { value: '', label: 'Tất cả trạng thái' },
  ...Object.values(PAYMENT_STATUS).map((s) => ({ value: s, label: s })),
];

export const TRANSACTION_FILTER_OPTIONS = [
  { value: '', label: 'Tất cả trạng thái' },
  ...Object.values(TRANSACTION_STATUS).map((s) => ({ value: s, label: s })),
];

// ─── New Loan Statuses (extended beyond enum) ─────────────────
export const LOAN_EXTENDED_STATUS = {
  ...LOAN_STATUS,
  APPROVED_LOCKED: 'Đã duyệt (đang khóa)',
  LOCK_EXPIRED: 'Hết khóa HM',
  WAITING_BANK_RESPONSE: 'Đã gửi ngân hàng',
  BANK_TIMEOUT: 'Timeout',
  DISBURSED_NEW: 'Đã giải ngân',
  DISBURSEMENT_FAILED_NEW: 'Giải ngân lỗi',
} as const;

// ─── Extended Loan Filter Options (11 filters) ───────────────
interface LoanFilterChip {
  key: string;
  label: string;
  statusMatch: readonly string[];
  disbursementMatch?: readonly string[];
}

interface DebtFilterChip {
  key: string;
  label: string;
  statusMatch: readonly string[];
  piMatch?: readonly string[];
}

interface HtxLimitFilterChip {
  key: string;
  label: string;
  statusMatch?: readonly string[];
  availableCheck?: boolean;
  lockedCheck?: boolean;
}

interface FarmerLimitFilterChip {
  key: string;
  label: string;
  riskMatch?: readonly string[];
  lockedCheck?: boolean;
  unlockedCheck?: boolean;
  overLimitCheck?: boolean;
}

interface SupplyFilterChip {
  key: string;
  label: string;
  statusMatch?: readonly string[];
  stockCheck?: 'zero' | 'low';
}

export const LOAN_FILTER_CHIPS: readonly LoanFilterChip[] = [
  { key: 'pending_check', label: 'Chờ kiểm tra', statusMatch: ['Chờ kiểm tra'] },
  { key: 'need_more', label: 'Cần bổ sung', statusMatch: ['Cần bổ sung'] },
  { key: 'approved_locked', label: 'Đã duyệt / Đang khóa', statusMatch: ['Đã duyệt', 'approved_locked'] },
  { key: 'lock_expired', label: 'Hết khóa', statusMatch: ['lock_expired'] },
  { key: 'pending_disbursement', label: 'Chờ giải ngân', statusMatch: ['Chờ giải ngân'] },
  { key: 'sent_to_bank', label: 'Đã gửi ngân hàng', statusMatch: ['waiting_bank_response'] },
  { key: 'waiting_bank', label: 'Chờ NH phản hồi', statusMatch: [], disbursementMatch: ['sent_to_bank', 'Đã gửi ngân hàng'] },
  { key: 'disbursed', label: 'Đã giải ngân', statusMatch: ['disbursed', 'Đã giải ngân'] },
  { key: 'disbursement_failed', label: 'Giải ngân lỗi', statusMatch: ['disbursement_failed', 'Giải ngân lỗi'] },
  { key: 'bank_timeout', label: 'Timeout', statusMatch: ['bank_timeout'] },
  { key: 'settled', label: 'Đã tất toán', statusMatch: ['Đã tất toán'] },
];

// ─── Debt Filter Options (8 filters) ─────────────────────────
export const DEBT_FILTER_CHIPS: readonly DebtFilterChip[] = [
  { key: 'active', label: 'Đang vay', statusMatch: ['Đang vay'] },
  { key: 'due_soon', label: 'Sắp đến hạn', statusMatch: ['Sắp đến hạn'] },
  { key: 'due', label: 'Đến hạn', statusMatch: ['Đến hạn'] },
  { key: 'overdue', label: 'Quá hạn', statusMatch: ['Quá hạn'] },
  { key: 'waiting_bank_credit', label: 'Chờ báo có', statusMatch: [], piMatch: ['qr_created', 'farmer_confirmed_transfer', 'waiting_bank_credit', 'bank_credit_received'] },
  { key: 'waiting_htx_confirmation', label: 'Chờ HTX xác nhận', statusMatch: [], piMatch: ['reconciled', 'waiting_htx_confirmation'] },
  { key: 'reconcile_failed', label: 'Đối soát lỗi', statusMatch: [], piMatch: ['reconcile_failed', 'complaint'] },
  { key: 'settled', label: 'Đã tất toán', statusMatch: ['Đã tất toán'], piMatch: ['settled'] },
];

// ─── HTX Limit Filter Options (6 filters) ────────────────────
export const HTX_LIMIT_FILTER_CHIPS: readonly HtxLimitFilterChip[] = [
  { key: 'active', label: 'Đang hiệu lực', statusMatch: ['Đang hiệu lực'] },
  { key: 'expiring_soon', label: 'Sắp hết hạn', statusMatch: ['Sắp hết hạn'] },
  { key: 'expired', label: 'Hết hạn', statusMatch: ['Đã kết thúc', 'Hết hạn'] },
  { key: 'paused', label: 'Tạm khóa', statusMatch: ['Tạm dừng'] },
  { key: 'available', label: 'Còn khả dụng', availableCheck: true },
  { key: 'locked', label: 'Đang khóa', lockedCheck: true },
];

// ─── Farmer Limit Filter Options (6 filters) ─────────────────
export const FARMER_LIMIT_FILTER_CHIPS: readonly FarmerLimitFilterChip[] = [
  { key: 'good', label: 'Tốt', riskMatch: ['Tốt'] },
  { key: 'watch', label: 'Cần chú ý', riskMatch: ['Cần chú ý', 'Khá'] },
  { key: 'high_risk', label: 'Rủi ro cao', riskMatch: ['Rủi ro cao', 'Nguy cơ'] },
  { key: 'locked', label: 'Đang khóa', lockedCheck: true },
  { key: 'unlocked', label: 'Hết khóa', unlockedCheck: true },
  { key: 'over_limit', label: 'Vượt hạn mức', overLimitCheck: true },
];

// ─── Supply Filter Options (6 filters) ───────────────────────
export const SUPPLY_FILTER_CHIPS: readonly SupplyFilterChip[] = [
  { key: 'pending', label: 'Chờ duyệt', statusMatch: ['Chờ duyệt'] },
  { key: 'issued', label: 'Đã cấp', statusMatch: ['Đã cấp'] },
  { key: 'partial', label: 'Cấp một phần', statusMatch: ['Cấp một phần'] },
  { key: 'rejected', label: 'Từ chối', statusMatch: ['Từ chối'] },
  { key: 'out', label: 'Hết hàng', statusMatch: ['Hết hàng'], stockCheck: 'zero' },
  { key: 'low', label: 'Sắp hết hàng', statusMatch: ['Sắp hết'], stockCheck: 'low' },
];

// ─── Expanded Loan Colors ────────────────────────────────────
const LOAN_EXTENDED_COLORS: Record<string, ColorPair> = {
  'Chờ kiểm tra': C.orange,
  'Cần bổ sung': C.amber,
  'Không đáp ứng': C.red,
  'Đã duyệt': C.agrida,
  'Đã duyệt (đang khóa)': { bg: 'bg-sky-100', text: 'text-sky-700' } as ColorPair,
  'Hết khóa HM': { bg: 'bg-orange-100', text: 'text-orange-700' } as ColorPair,
  'Đã gửi ngân hàng': C.sky,
  'Chờ giải ngân': C.sky,
  'Đã giải ngân': C.green,
  'Giải ngân lỗi': C.red,
  'Timeout': C.amber,
  'Đã tất toán': C.green,
  'Từ chối': C.red,
  'Đã cấp vật tư': C.agrida,
  'Chờ nhập kho': C.amber,
  'Đã hủy': C.slate,
  'Quá hạn': C.red,
  'approved_locked': { bg: 'bg-sky-100', text: 'text-sky-700' } as ColorPair,
  'waiting_bank_response': C.sky,
  'disbursed': C.green,
  'disbursement_failed': C.red,
  'bank_timeout': C.amber,
  'lock_expired': C.orange,
  // Debt filter display aliases
  'Chờ báo có': { bg: 'bg-sky-100', text: 'text-sky-700' } as ColorPair,
  'Chờ HTX xác nhận': { bg: 'bg-indigo-100', text: 'text-indigo-700' } as ColorPair,
  'Đối soát lỗi': { bg: 'bg-red-100', text: 'text-red-700' } as ColorPair,
  // HTX limit filter display aliases
  'Hết hạn': C.red,
  'Còn khả dụng': C.green,
  'Đang khóa': { bg: 'bg-orange-100', text: 'text-orange-700' } as ColorPair,
  // Farmer limit filter display aliases
  'Vượt hạn mức': C.red,
  'Hết khóa': C.orange,
  // Supply filter display aliases
  'Chờ duyệt': { bg: 'bg-sky-100', text: 'text-sky-700' } as ColorPair,
  'Đã cấp': C.green,
  'Cấp một phần': { bg: 'bg-amber-100', text: 'text-amber-700' } as ColorPair,
  'Hết hàng': C.red,
  'Sắp hết hàng': C.amber,
};

// Merge extended colors into LOAN_COLORS for consistent lookup
const _LOAN_FULL_COLORS: Record<string, ColorPair> = { ...LOAN_COLORS };
Object.entries(LOAN_EXTENDED_COLORS).forEach(([k, v]) => {
  _LOAN_FULL_COLORS[k] = v;
});
